import { Component, Prop } from 'vue-property-decorator'
import { CreateElement } from 'vue'
import { Body1, H6, Subtitle1 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import { Card } from '@src/components/card/card'
import { Button, MenuButton } from '@src/elements/button/button'
import { Popover } from '@src/components/popover/popover'
import { OptionGroup } from '@src/elements/select/option-group'
import { DropdownOption } from '@src/elements/select/option'
import AssignToMember from '@src/pages/assign-to-member/assign-to-member'
import TrainingPlan from '@src/data/training-plan'
import { IntersectionsPopup } from '@src/components/intersections-popup/intersections-popup'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { noop } from '@src/util/noop'
import { CheckupState } from '@src/data/checkup'

import Member from '@src/data/member'
import { TrainingPlanService } from '@src/service/training-plan'
import { NotificationTypes } from '@src/util/constants'

import EventBus from '@src/lib/event_bus'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Modal } from '@src/elements/modal/modal'
import SessionManager from '@src/lib/session_manager'
import { MemberProfile } from '@src/data/member-profile'
import { MemberTrainingPlanDTO } from '@src/routes/content/components/training-plan-builder/save-plan'
import { convertLocalDateToDate, convertToLocalDate, convertToYYYYMMDD } from '@src/util/date-format'
import { ReviewPopup } from '@src/components/review-popup/review-popup'
import { DatePicker } from '@src/elements/input/date-picker'

import '../training-card.sass'

@Component({
  computed: {
    member: {
      get: function () {
        return this.$store.state.selectedMember || this.$store.state.memberProfile || this.$store.state.trainingMember
      }
    }
  }
})
export class TrainingCardInfo extends Card {
  @Prop({ required: true })
  training: TrainingPlan

  @Prop({ required: false, default: false })
  isSelected: boolean

  @Prop({ required: false, default: noop })
  updateCallback: (trainingId: number | null, name?: string, totalDays?: number | null) => void

  @Prop({ required: true })
  type: 'view' | 'active' | 'select' | 'history'

  protected notificationType: string = ''
  protected confirmMessage: string = ''
  protected showConfirmPopup: boolean = false
  // handle plan intersections
  protected showIntersectionsPopup: boolean = false
  protected intersections: TrainingPlan[] = []
  protected overwritingTrainingPlan: MemberTrainingPlanDTO
  protected member: Member | MemberProfile | null
  protected showEquipmentPopup: boolean = false

  protected service: TrainingPlanService = new TrainingPlanService()
  protected showReviewPopup: boolean = false
  protected showReactivateModal: boolean = false
  private startDateTP: string = ''
  private endDateTP: string = ''

  constructor (props) {
    super(props)
  }

  render (h: CreateElement) {
    return <Card class='training-card training-card-info' click={this.selectTraining}>
      {this.renderHeader(h)}
      {this.renderPreview(h)}
      {this.renderTitle(h)}
      {this.renderSpecs(h)}
      {this.renderRight(h)}
      {this.type === 'history' && this.renderReactivateOption(h) }
    </Card>
  }

  renderHeader (h: CreateElement) {
    const summary = this.training.summary
    return <div slot='header' class='training-plan-detail plan-days-summary header'>
        <Subtitle1 class='training-plan-detail-text' ref='days'><b>{summary.totalDays} {this.$t('DAYS')}</b></Subtitle1>
        <Subtitle1 class='training-plan-detail-text'
                   ref='workout_days'>{summary.workoutDays} {this.$t('PAGE.CONTENT.TRAINING_PLAN.WORKOUT_DAYS')}</Subtitle1>
        <Subtitle1 class='training-plan-detail-text'
                   ref='rest_days'>{summary.restDays} {this.$t('PAGE.CONTENT.TRAINING_PLAN.REST_DAYS')}</Subtitle1>
      </div>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview' class='training-preview'>
      <img src={this.training.displayImage} />
    </div>
  }

  renderTitle (h: CreateElement) {
    return <H6 class='title' slot='title'>
      <div>{this.training.name}</div>
    </H6>
  }

  renderSpecs (h: CreateElement) {

    const summary = this.training.summary

    const firstItemsEquipment = this.training.equipment.slice(0, 5).map((equipmentName) => {
      return <div class='equipment'>
        <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}>
          <Icon name={'equipment/' + equipmentName} />
        </Tooltip>
      </div>
    })

    const equipment = this.training.equipment.map((equipmentName) => {
      return <div class='equipment'>
        <Icon name={'equipment/' + equipmentName} />
        <Body1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}</Body1>
      </div>
    })

    return <div slot='specs' class='specs'>
      <div class='training-plan-detail'>
        <Icon name='workout/exercises' /><Subtitle1 ref='total_workouts'
                                                    class='training-plan-detail-text'>{summary.workouts} {this.$t('WORKOUTS')}</Subtitle1>
      </div>
      <div class='training-plan-detail'>
        <Icon name='workout/time' /><Subtitle1 ref='mins_avg'
                                               class='training-plan-detail-text'>{summary.duration} {this.$t('PAGE.CONTENT.TRAINING_PLAN.MINS_AVG')}</Subtitle1>
      </div>
      <div class='training-plan-detail'>
        <Icon name='workout/score' /><Subtitle1 ref='total_met'
                                                class='training-plan-detail-text'>{summary.estimatedMetValue} {this.$t('UNITS.MET')}</Subtitle1>
      </div>
      {/*Hide home workouts*/}
      {/*{summary.homeWorkouts > 0 ?
        <div class='tracked'>
          <Subtitle1 ref='home_workouts'>{summary.homeWorkouts} {this.$t('PAGE.CONTENT.WORKOUTS.TYPES.HOME_WORKOUTS')}</Subtitle1>
        </div> : null}
      {summary.trackedWorkouts > 0 ?
        <div class='tracked'>
          <Subtitle1 ref='station_workouts'>{summary.trackedWorkouts} {this.$t('PAGE.CONTENT.WORKOUTS.TYPES.STATION_WORKOUTS')}</Subtitle1>
        </div> : null}*/}
      {this.type === 'active' && this.training.startDate &&
      <div class='dates'>
        <div>
          <span class='bold'>{`${this.$t('START_DATE')}:`}</span>{convertToLocalDate(this.training.startDate)}
        </div>
        <div>
          <span class='bold'>{`${this.$t('END_DATE')}:`}</span>{convertToLocalDate(this.training.endDate)}
        </div>
      </div>}
      <div class='equipmentWrapper'>{firstItemsEquipment}{this.training.equipment.length > 5 ?
        <Icon class='see-more' name='util/see-more' click={this.invertShowEquipment} /> : ''} </div>
      <Modal id={'tp-equipment-modal'} class={'training-equipment-modal'} show={this.showEquipmentPopup} closeCallback={this.invertShowEquipment}
             title={this.$t('EQUIPMENT')}>
        {equipment}
      </Modal>
    </div>
  }

  renderRight (h: CreateElement) {
    // the right button section is different for each type of card
    if (this.type === 'view') {
      return this.renderRightView(h)
    }
    if (this.type === 'active') {
      return this.renderRightActive(h)
    }
    if (this.type === 'select') {
      return this.renderRightSelect(h)
    }
  }

  renderRightView (h: CreateElement) {
    const summary = this.training.summary
    return <div slot='right' class='more-options'>
      <MenuButton>
        <Popover>
          <OptionGroup>
            {<DropdownOption class='assign-to-member-header'
                            label={this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_TO_MEMBER')}>
              <AssignToMember totalDays={summary.totalDays} callback={this.assignToMember} />
            </DropdownOption>}
            {!SessionManager.Instance.isAssistant() ? <div class='edit-training'
                 onClick={this.editTrainingPlan}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.EDIT')}</div> : null}
            <div class='view-training'
                 onClick={this.viewTrainingPlan}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.VIEW')}</div>
            {!SessionManager.Instance.isAssistant() && <div class='remove-training'
                 onClick={() => this.showCloseConfirm(this.$t('PAGE.CONTENT.TRAINING_PLAN.CONFIRM_REMOVE'))}>
              {this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.REMOVE')}
            </div>}
            {!SessionManager.Instance.isAssistant() ? <div class='clone-training' onClick={this.clone}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.CLONE')}</div> : null}
          </OptionGroup>
        </Popover>
      </MenuButton>
      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.removeTrainingPlan}
                    message={this.confirmMessage}
                    type={this.notificationType} />
      {this.showIntersectionsPopup ?
        <IntersectionsPopup show={true}
                            closeCallback={this.closeIntersectionsPopup}
                            member={this.member}
                            intersections={this.intersections}
                            overwritingTrainingPlan={this.overwritingTrainingPlan} /> : null}
      {this.showReviewPopup ?
        <ReviewPopup show={true}
                     trainingPlanId = {this.training.id}
                     member={this.member}
                     closeCallback={this.closeReviewTrainingPopup}/> : null}
    </div>
  }

  renderRightActive (h: CreateElement) {
    const { path } = this.$route || { path: undefined }
    let checkup = this.$store.state.checkup
    const isCheckup = path && path.includes('checkup')

    let enableEditRemove = (SessionManager.Instance.isManager() ? !SessionManager.Instance.isAssistant() : (this.member ? this.member.memberPermissions.createTrainingPlan : false))
    if (isCheckup) {
      enableEditRemove = (SessionManager.Instance.isManager() ? !SessionManager.Instance.isAssistant() : false)
    }

    const isEditable = enableEditRemove && (!checkup || checkup.state !== CheckupState.COMPLETED || !isCheckup)

    return <div slot='right' class='more-options'>
      <MenuButton>
        <Popover>
          <OptionGroup>
            {isEditable ? <div class='edit-training' onClick={this.editTrainingPlan}>
              {this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.EDIT')}</div> : null}
            <div class='view-training'
                 onClick={this.viewTrainingPlan}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.VIEW')}</div>
            {isEditable ? <div class='remove-training'
                 onClick={() => this.showCloseConfirm(this.$t('PAGE.CONTENT.TRAINING_PLAN.CONFIRM_REMOVE'))}>
              {this.$t('PAGE.CONTENT.TRAINING_PLAN.ACTIONS.REMOVE')}</div> : null }
          </OptionGroup>
        </Popover>
      </MenuButton>
      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.removeTrainingPlan}
                    message={this.confirmMessage}
                    type={this.notificationType} />
      {this.showIntersectionsPopup ?
        <IntersectionsPopup show={true}
                            closeCallback={this.closeIntersectionsPopup}
                            member={this.member}
                            intersections={this.intersections}
                            overwritingTrainingPlan={this.overwritingTrainingPlan} /> : null}
      {this.showReviewPopup ?
        <ReviewPopup show={true}
                     trainingPlanId = {this.training.id}
                     member={this.member}
                     closeCallback={this.closeReviewTrainingPopup}/> : null}
    </div>
  }

  renderRightSelect (h: CreateElement) {
    return <div slot='right' class='more-options'>
      {this.isSelected ? <Icon class='selected-btn' name='/util/selected' /> : ''}
      {this.showIntersectionsPopup ?
        <IntersectionsPopup show={true}
                            closeCallback={this.closeIntersectionsPopup}
                            member={this.member}
                            intersections={this.intersections}
                            overwritingTrainingPlan={this.overwritingTrainingPlan} /> : null}
      {this.showReviewPopup ?
        <ReviewPopup show={true}
                     trainingPlanId = {this.training.id}
                     member={this.member}
                     closeCallback={this.closeReviewTrainingPopup}/> : null}
    </div>
  }

  renderReactivateOption (h) {
    // trainers and members with "Assign Individual Training Plan" permission can reactivate tps
    if (SessionManager.Instance.isManager() || this.member && this.member.memberPermissions.changeIndividualTrainingPlan) {
      return (<div slot='expanded' class='reactivate-tp-section'>
      <Button class='reactivate-tp-btn' label={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REACTIVATE')} click={this.openReactivateModal} type='elevated' />
      {this.showIntersectionsPopup ?
        <IntersectionsPopup show={true}
                            closeCallback={this.closeIntersectionsPopup}
                            member={this.member}
                            intersections={this.intersections}
                            overwritingTrainingPlan={this.overwritingTrainingPlan} /> : null}
      {this.showReviewPopup ?
        <ReviewPopup show={true}
                     trainingPlanId = {this.training.id}
                     member={this.member}
                     closeCallback={this.closeReviewTrainingPopup}/> : null}
      {this.showReactivateModal && this.renderDatePickModal(h)}
    </div>)
    }
  }

  renderDatePickModal (h: CreateElement) {
    return <Modal class='reactivate-tp' show={true} title={`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REACTIVATE')} ${this.training.name}`} overflow={'hidden'} closeCallback={this.closeReactivateModal}>
      <div class='date-pickers'>
        <DatePicker allowPastDate={false} type='date' value={this.startDateTP} popper-class={'over-modal'} label={`${this.$t('START_DATE')}:`}
                    change={(value: string) => {
                      this.startDateTP = this.normalizeArabicNumerals(value.toString())
                      this.computeEndDateTrainingPlan()
                    }} />
        <DatePicker readonly={true} allowPastDate={false} type='date' value={this.endDateTP} popper-class={'over-modal'} label={`${this.$t('END_DATE')}:`}/>
      </div>
      <div slot='footer' class='btn-row'>
        <Button label={this.$t('CANCEL')} click={this.closeReactivateModal} type='outline' />
        <Button class='confirm-btn' label={this.$t('SAVE')} click={() => this.assignToMember(this.member!, this.startDateTP)} type='elevated' disabled={!this.startDateTP} />
      </div>
    </Modal>
  }

  showCloseConfirm (message: any) {
    this.confirmMessage = message
    this.notificationType = NotificationTypes.CONFIRM
    this.showConfirmPopup = true
  }

  assignToMember (member: Member, startDate: string): any {
    if (!member) {
      // cannot assign to no member
      return
    } else {
      // set training member from my content
      this.$store.dispatch('setMemberForActiveTraining', member)
    }

    if (!startDate) {
      // plans need to have a start date
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.ASSIGNATION_FAILED'), fadeOut: false })
      return
    }

    // get training plan details
    return this.service.getTrainingPlan(this.training.id)
      .then(async (trainingPlan) => {
        // assign to member
        const normalizedStartDate = this.normalizeArabicNumerals(startDate)
        const assignResponse = await this.service.assignTrainingPlanToMember(this.training.name, convertToYYYYMMDD(normalizedStartDate),
          this.formatTrainingDays(trainingPlan ? trainingPlan.trainingDays : []), member.id, true)
        this.closeReactivateModal()
        if (Object.keys(assignResponse.training_days).length) {
          // failed to assign - plan has deleted workouts
          this.showReviewTrainingPopup()
        } else {
          if (assignResponse.intersections.length) {
            // failed to assign: if the plan intersects, show the popup to allow the user to decide the further actions
            this.intersections = assignResponse.intersections
            this.overwritingTrainingPlan = assignResponse.trainingPlanData
            this.showIntersectionsNotification()
          } else {
            // successful assign
            this.$store.dispatch('showToast', {
              message: this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : '') }),
              fadeOut: true
            })
            this.$store.dispatch('setMemberForActiveTraining', null)
            EventBus.trigger('reloadSelectedMember')
          }
        }
      })
  }

  /**
   * Remove the member owned plan (unassign plan from member).
   *
   * - if the plan is started -> will drop current day, future days and if needed will set 0 rest days for the last survived day
   * - if the plan is not started -> will remove plan
   * @param confirm
   */
  removeTrainingPlan (confirm: boolean) {
    this.showConfirmPopup = false

    if (confirm === true) {
      return this.service.delete(this.training.id)
        .then(() => {
          if (this.type === 'view') {
            if (this.updateCallback && this.training.id) {
              this.updateCallback(this.training.id)
              this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.TRAINING_PLAN.DELETE_SUCCESS'), fadeOut: true })
            }
          } else if (this.type === 'active') {
            EventBus.trigger('reloadSelectedMember')
            this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
          }
        })
    }
  }

  async clone () {
    let trainingPlan = this.training
    const trainingDays = await this.service.getTrainingPlanDays(this.training.id)
    trainingPlan.trainingDays = trainingDays || []
    trainingPlan.id = null

    this.$store.dispatch('setSelectedTrainingPlan', { trainingPlan })
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/trainingPlan`)

  }

  /**
   * Retrieve the training plan details and open the plan builder.
   */
  editTrainingPlan () {
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/trainingPlan/edit/${this.training.id}`)
  }

  viewTrainingPlan () {
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/trainingPlan/view/${this.training.id}`)
  }

  selectTraining () {
    if (this.type === 'select') {
      // select/deselect
      this.updateCallback(this.isSelected ? null : this.training.id, this.training.name, this.training.summary.totalDays)
    }
  }

  showIntersectionsNotification () {
    this.showIntersectionsPopup = true
  }

  closeIntersectionsPopup (successfulAssign: boolean) {
    if (successfulAssign) {
      this.showIntersectionsPopup = false
      this.intersections = []
      this.$store.dispatch('showToast', {
        message: this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : '') }),
        fadeOut: true
      })
    } else {
      this.showIntersectionsPopup = false
    }
    this.$store.dispatch('setMemberForActiveTraining', null)
  }

  showReviewTrainingPopup () {
    this.showReviewPopup = true
  }

  closeReviewTrainingPopup () {
    this.$store.dispatch('setMemberForActiveTraining', null)
    this.showReviewPopup = false
  }

  formatTrainingDays (days) {
    let trainingDays: any[] = []
    days.forEach(day => {
      trainingDays.push({
        workout_id: day.workout.id,
        rest_days: day.rest_days
      })
    })
    return trainingDays
  }

  invertShowEquipment () {
    this.showEquipmentPopup = !this.showEquipmentPopup
  }

  openReactivateModal () {
    this.showReactivateModal = true
  }

  closeReactivateModal () {
    this.showReactivateModal = false
    this.startDateTP = ''
    this.endDateTP = ''
  }

  computeEndDateTrainingPlan () {
    let selectedDate = convertLocalDateToDate(this.startDateTP)
    selectedDate.setDate(selectedDate.getDate() + this.training.totalDayCount - 1)
    this.endDateTP = convertToLocalDate(selectedDate)
  }

  normalizeArabicNumerals (value: string): string {
    if (!value) return value
    // Convert Arabic-Indic numerals (٠١٢٣٤٥٦٧٨٩) to Western numerals (0123456789)
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
    let normalized = value
    arabicNumerals.forEach((arabicNum, index) => {
      normalized = normalized.replace(new RegExp(arabicNum, 'g'), index.toString())
    })
    return normalized
  }

}
